// Lucid chart to visualize this authentication workflow
// https://lucid.app/lucidchart/a8004b41-0efb-4b38-ae49-cbff64c8a1a1/edit?viewport_loc=-614%2C-1139%2C7721%2C3766%2C0_0&invitationId=inv_71011c85-1db8-4ff4-a7ca-72a52e84769e
//
// Public auth facade. Dispatches to a provider module (cognitoProvider.js /
// stytchProvider.js) based on VITE_AUTH_PROVIDER; session.js holds the
// storage helpers neither provider needs to own. Callers everywhere else
// (views, router, stores) should only ever import from here.

import { jwtDecode } from 'jwt-decode';
import router from '../../router/index';
import * as cognitoProvider from './cognitoProvider';
import * as stytchProvider from './stytchProvider';
import {
  clearTokens,
  currentSession,
  isSessionExpired,
  markSessionExpired,
  SessionExpiredError,
} from './session';

const AUTH_PROVIDER = import.meta.env.VITE_AUTH_PROVIDER || 'cognito';

export function isStytchProvider() {
  return AUTH_PROVIDER === 'stytch';
}

const provider = isStytchProvider() ? stytchProvider : cognitoProvider;

export const signIn = provider.signIn;

// Stytch-only, and safe to call under Cognito — returns '' there, which callers
// already treat as "render the login page instead". See stytchProvider.
export const resolveSsoConnectionId = stytchProvider.resolveSsoConnectionId;

// Stytch-only, and safe to call under Cognito — returns [] there, so LoginView
// renders no SSO buttons without branching on provider. See stytchProvider.
export const listSsoConnections = stytchProvider.listSsoConnections;

// Runtime-resolved variant (live from auxo-api, build-time fallback) — resolves
// immediately under Cognito or when the endpoint isn't configured.
export const fetchSsoConnections = stytchProvider.fetchSsoConnections;

// The tier's breakglass split (CPRD-5849), derived from the build MODE —
// LoginView's collapse logic and the widget's product list share this one flag.
export const stytchPasswordsEnabled = stytchProvider.stytchPasswordsEnabled;

// Wraps provider.signOut so a Stytch session bootstrapped via
// mountAdminPortalSSO always gets revoked on logout, even under Cognito
// (which has no knowledge such a session exists) — see
// stytchProvider.revokeBootstrappedSession.
export async function signOut() {
  await stytchProvider.revokeBootstrappedSession();
  return provider.signOut();
}

// Stytch-only, and safe to call under Cognito — returns false there, so the
// router falls through to /unauthorized as before. See stytchProvider.
export const restoreSession = stytchProvider.restoreSession;

export const getTokens = provider.getTokens;
export const isAuthenticated = provider.isAuthenticated;

// No-ops under Cognito (see stytchProvider.mountManualLogin), so callers
// don't need to branch on provider before calling this.
export const mountManualLogin = stytchProvider.mountManualLogin;

// No-op when Stytch isn't onboarded for this client at all (see
// stytchProvider.mountAdminPortalSSO) — works under both auth providers.
export const mountAdminPortalSSO = stytchProvider.mountAdminPortalSSO;

export { clearTokens, currentSession, SessionExpiredError };

// Cognito's OAuth redirect uses ?code=..., Stytch's SSO redirect uses
// ?token=... — the router shouldn't need to know either param name.
export function getCallbackToken(query) {
  return query.code || query.token;
}

export async function getIdToken() {
  let token = sessionStorage.getItem('id_token');

  // no token in session storage
  if (!token) {
    console.error(
      'No JWT found, session is not active. Please login through your IDP'
    );

    const currentPath = router.currentRoute.value.path;
    if (currentPath === '/logout' || currentPath === '/login') return null;
    endSession();
  }

  // check if token is expired
  let decoded_token = jwtDecode(token);
  let is_token_expired = Date.now() > decoded_token.exp * 1000;

  // token is not expired
  if (!is_token_expired) return token;

  // A refresh already failed: the session is dead, so don't ask Stytch again.
  if (isSessionExpired()) throw new SessionExpiredError();

  // token is expired - attempt to refresh
  token = await refreshToken();
  if (!token) endSession();
  return token;
}

// Throws rather than returning, so the caller's request is never sent with a
// missing token (which would 401 and toast on top of the redirect).
function endSession() {
  // Callers that shared one failed refresh all land here; redirect only once.
  if (!isSessionExpired()) {
    markSessionExpired();
    router.push('/unauthorized');
  }
  throw new SessionExpiredError();
}

// Shared so a tab waking with several requests at once makes one /auth/token call.
let refreshInFlight = null;

export function refreshToken() {
  refreshInFlight ??= _refreshToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function _refreshToken() {
  let token = null;
  try {
    const refresh_token = sessionStorage.getItem('refresh_token');
    await getTokens(null, refresh_token);

    token = sessionStorage.getItem('id_token');
  } catch (error) {
    console.error(
      'Error refreshing token.  Please login through your IDP: ',
      error
    );
    return null;
  }
  return token;
}

export async function getAuthenticatedHeaders() {
  let id_token = await getIdToken();
  const reqData = {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json, text/plain, */*',
      Authorization: `Bearer ${id_token}`,
      redirect: 'follow',
    },
    response: true,
  };
  return reqData;
}
